// Opens the executive deck from the ROI summary step (the deck is its own full-screen `/story`
// route: the slides are fixed 1920x1080 inside <deck-stage> and the leave-behind PDF / ROI one-pager
// are window.print() of that page). Builds the v3 value story, writes it to `clay-value-story-v1`,
// and navigates to `/story`.
//
// The once-per-deal review gate stands in front of that one action, exactly where v2 put it (before
// the deck exists, never on the customer-facing slides). A null deal id (demo, no account) always
// shows the gate and stores nothing. The shell owns the button and renders the dialog; this hook
// owns the state and the two-step flow.

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppBasePath } from '../../../_shared/base-path';
import { loadBenchmarkRows } from '../../state/benchmark-store';
import { writeStoryForDeck } from '../../state/deck-handoff';
import { sizingOf, useCalculatorStore } from '../../state/calculator-store';
import { reviewDealId } from '../calculator/review-gate';
import { ackReviewGate, reviewGateNeeded } from './deck/review-gate-storage';

export interface OpenDeck {
  /** The footer button's action: the gate first when this deal has not acknowledged it, else straight in. */
  open: () => void;
  gateOpen: boolean;
  /** The SE acknowledged the gate: remember it for the deal and open the deck. */
  acknowledge: () => void;
  /** Browser storage was unavailable, so the story could not be handed to the deck. */
  failed: boolean;
}

export function useOpenDeck(): OpenDeck {
  const basePath = useAppBasePath();
  const navigate = useNavigate();
  const [gateOpen, setGateOpen] = useState(false);
  const [failed, setFailed] = useState(false);
  const account = useCalculatorStore((s) => s.account);

  const dealId = reviewDealId(
    account?.kind === 'account' ? { id: account.accountId, opportunityId: account.opportunityId } : null,
  );

  function openDeck() {
    const ok = writeStoryForDeck(localStorage, sizingOf(useCalculatorStore.getState()), {
      created: Date.now(),
      customerName: account?.kind === 'account' ? account.accountName : '',
      benchmarkRows: loadBenchmarkRows(),
    });
    setFailed(!ok);
    if (ok) navigate(`${basePath}/story`);
  }

  return {
    open: () => {
      if (reviewGateNeeded(dealId)) setGateOpen(true);
      else openDeck();
    },
    gateOpen,
    acknowledge: () => {
      ackReviewGate(dealId);
      setGateOpen(false);
      openDeck();
    },
    failed,
  };
}
